import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { mapPasswordResetErrorToResource } from '../../../constants/resources';
import { ModalFragmentProps } from '../../../constants/types';
import useAccountSecurityPromptContext from '../../../hooks/useAccountSecurityPromptContext';
import { AccountSecurityPromptActionType } from '../../../store/action';
import ModalState from '../../../store/modalState';
import { FragmentModalHeader, HeaderButtonType } from '../fragments/modalHeader';

/**
 * The confirmation page of the add email modal flow.
 */
const ModalAddEmailConfirmation: React.FC<ModalFragmentProps> = ({
  closeModal
}: ModalFragmentProps) => {
  const {
    state: { resources, requestService, emailAddress },
    dispatch
  } = useAccountSecurityPromptContext();

  /*
   * Component State
   */

  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestInFlight, setRequestInFlight] = useState<boolean>(false);

  /*
   * Event Handlers
   */

  const resendEmail = async () => {
    setRequestError(null);
    setRequestInFlight(true);
    const result = await requestService.resetSendPrompted();
    setRequestInFlight(false);
    if (result.isError) {
      setRequestError(mapPasswordResetErrorToResource(resources, result.error));
    }
  };

  const changeEmail = () =>
    // Transition: Email needs to be changed.
    dispatch({
      type: AccountSecurityPromptActionType.SET_MODAL_STATE,
      modalState: ModalState.ADD_EMAIL_FORM
    });

  /*
   * Component Markup
   */

  return (
    <React.Fragment>
      <FragmentModalHeader
        headerText={resources.Header.CheckYourEmail}
        buttonType={HeaderButtonType.CLOSE}
        buttonAction={closeModal}
        buttonEnabled
        headerInfo={null}
      />
      <Modal.Body>
        <div className='modal-shield' />
        <p
          className='modal-margin-bottom-xlarge'
          // It's not ideal to directly template HTML tags for this translation
          // resource. There is a documented `useTranslation` hook that is
          // available e.g. in the `creator-dashboard` repo but not here.
          // TODO: Find a better way to template this resource.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: resources.Description.CheckYourEmail(emailAddress) }}
        />
        <div className='modal-margin-bottom-xlarge'>
          {requestInFlight ? (
            <span className='spinner spinner-xs spinner-no-margin' />
          ) : (
            <button type='button' className='modal-body-button-link' onClick={resendEmail}>
              {resources.Action.ResendPasswordResetEmail}
            </button>
          )}
          <p className='text-error xsmall'>{requestError}</p>
        </div>
        <button
          type='button'
          className='modal-body-button-link small'
          onClick={changeEmail}
          disabled={requestInFlight}>
          {resources.Action.ChangeEmail}
        </button>
      </Modal.Body>
    </React.Fragment>
  );
};

export default ModalAddEmailConfirmation;
